
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Helmet } from "react-helmet";
import { BagCard } from "@/components/BagCard";
import { supabase } from "@/integrations/supabase/client";
import { BagData } from "@/types/bag";
import { useNavigate } from "react-router-dom";
import { AboutCreator } from "@/components/AboutCreator";

export default function SearchResults() {
  const { query } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState<BagData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Sort search results to prioritize exact matches
  const sortSearchResults = (results: BagData[], query: string): BagData[] => {
    const normalizedQuery = query.trim().toLowerCase();
    
    return [...results].sort((a, b) => {
      // Check for exact match in Name (case-insensitive)
      const aNameExactMatch = a.Name?.toLowerCase().includes(normalizedQuery) || false;
      const bNameExactMatch = b.Name?.toLowerCase().includes(normalizedQuery) || false;
      
      // Check for exact match in style_number
      const aStyleExactMatch = a.style_number?.toString() === normalizedQuery;
      const bStyleExactMatch = b.style_number?.toString() === normalizedQuery;
      
      // Priority 1: Exact matches in style number (highest priority)
      if (aStyleExactMatch && !bStyleExactMatch) return -1;
      if (!aStyleExactMatch && bStyleExactMatch) return 1;
      
      // Priority 2: Exact matches in name (second-highest priority)
      if (aNameExactMatch && !bNameExactMatch) return -1;
      if (!aNameExactMatch && bNameExactMatch) return 1;
      
      // Priority 3: Name contains the query at the start
      const aNameStartsWith = a.Name?.toLowerCase().startsWith(normalizedQuery) || false;
      const bNameStartsWith = b.Name?.toLowerCase().startsWith(normalizedQuery) || false;
      
      if (aNameStartsWith && !bNameStartsWith) return -1;
      if (!aNameStartsWith && bNameStartsWith) return 1;
      
      // Priority 4: Alphabetical order by name
      if (a.Name && b.Name) {
        return a.Name.localeCompare(b.Name);
      }
      
      // If no name, sort by style number
      return (a.style_number || 0) - (b.style_number || 0);
    });
  };

  useEffect(() => {
    const fetchResults = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .rpc('search_bags', {
          search_query: query || ''
        });

      if (error) {
        console.error('Error searching bags:', error);
        return;
      }

      // Apply the sorting to prioritize exact matches
      const sortedResults = sortSearchResults(data || [], query || '');
      setResults(sortedResults);
      setIsLoading(false);
    };

    if (query) {
      fetchResults();
    }
  }, [query]);

  return (
    <>
      <Helmet>
        <title>Search Results for "{query}" - Vintage Coach Bags</title>
        <meta 
          name="description" 
          content={`Search results for "${query}" in our vintage Coach bag database. Find details, measurements, and authenticity features for Coach bags matching your search.`} 
        />
      </Helmet>
      <main className="min-h-screen w-full bg-background flex flex-col">
        <div className="flex-grow container px-4 pt-20 pb-12 mx-auto">
          <Header />
          
          {isLoading ? (
            <div className="text-center mt-8">
              <p className="text-lg text-muted-foreground">Loading results...</p>
            </div>
          ) : results.length > 0 ? (
            <div className="mt-8">
              <h2 className="text-2xl font-display mb-6">Search Results for "{query}"</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {results.map((bag) => (
                  <BagCard key={bag.id} bag={bag} />
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-8 text-center">
              <p className="text-lg text-muted-foreground">No results found for "{query}"</p>
            </div>
          )}
        </div>
        <AboutCreator />
      </main>
    </>
  );
}
